import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, CheckCircle2, AlertTriangle, XCircle, FileText, Download } from "lucide-react";
import { toast } from "sonner";

const EXAMPLE = `email,first_name,last_name,city,postal_code
alice.martin@example.com,Alice,Martin,Chartres,28000
paul.durand@example.com,Paul,Durand,Chartres,28000
julie.petit@example.com,Julie,Petit,Dreux,28100`;

export default function AdminImportBeneficiaries() {
  const [codes, setCodes] = useState([]);
  const [codeId, setCodeId] = useState("");
  const [file, setFile] = useState(null);
  const [optin, setOptin] = useState(true);
  const [sendMail, setSendMail] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/admin/access-codes").then((r) => {
      const active = r.data.filter((c) => c.status === "active");
      setCodes(active);
      if (active.length) setCodeId(active[0].id);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!codeId) { toast.error("Sélectionnez un code d'accès."); return; }
    if (!file) { toast.error("Sélectionnez un fichier CSV."); return; }
    setLoading(true);
    setResult(null);
    const form = new FormData();
    form.append("access_code_id", codeId);
    form.append("alerts_optin", optin ? "true" : "false");
    form.append("send_email_flag", sendMail ? "true" : "false");
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/beneficiaries/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.created} bénéficiaire(s) créé(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur import");
    } finally { setLoading(false); }
  };

  const downloadExample = () => {
    const blob = new Blob([EXAMPLE], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "exemple-beneficiaires.csv";
    a.click();
  };

  const selectedCode = codes.find((c) => c.id === codeId);

  return (
    <div>
      <div className="flex items-center gap-3">
        <UploadCloud className="text-[#E05638]" size={26}/>
        <h1 className="font-heading text-3xl font-bold" data-testid="admin-import-title">Import CSV bénéficiaires</h1>
      </div>
      <p className="text-sm text-slate-500 mt-1 max-w-3xl">
        Importez en masse les salariés, adhérents ou agents d'une structure partenaire.
        Chaque ligne du CSV créera un compte avec un mot de passe auto-généré envoyé par email.
      </p>

      <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 space-y-5 max-w-3xl" data-testid="admin-import-form">
        <div>
          <Label>Code d'accès à rattacher</Label>
          <Select value={codeId} onValueChange={setCodeId}>
            <SelectTrigger data-testid="import-code-select"><SelectValue placeholder="Sélectionner"/></SelectTrigger>
            <SelectContent>
              {codes.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.code} — {c.affiliation_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedCode && (
            <div className="mt-2 text-xs text-slate-500">
              Rattachement automatique : <strong>{selectedCode.affiliation_name}</strong> ({selectedCode.affiliation_type})
              · Utilisations actuelles : {selectedCode.used_count || 0}{selectedCode.max_uses ? ` / ${selectedCode.max_uses}` : ""}
            </div>
          )}
        </div>

        <div>
          <Label>Fichier CSV</Label>
          <div className="mt-1 flex items-center gap-3">
            <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={(e)=>setFile(e.target.files?.[0] || null)}
                   data-testid="import-file-input" className="hidden"/>
            <Button type="button" variant="outline" onClick={()=>inputRef.current?.click()}>
              <FileText size={16} className="mr-1"/> {file ? file.name : "Parcourir…"}
            </Button>
            <button type="button" onClick={downloadExample} className="text-xs text-[#E05638] font-semibold hover:underline">
              Télécharger un exemple
            </button>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Colonnes attendues : <code className="bg-slate-100 px-1 rounded">email</code>,{" "}
            <code className="bg-slate-100 px-1 rounded">first_name</code>,{" "}
            <code className="bg-slate-100 px-1 rounded">last_name</code>,{" "}
            <code className="bg-slate-100 px-1 rounded">city</code>,{" "}
            <code className="bg-slate-100 px-1 rounded">postal_code</code>. Séparateur virgule ou point-virgule.
          </div>
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={optin} onChange={(e)=>setOptin(e.target.checked)} className="w-4 h-4" data-testid="import-optin"/>
            Activer les alertes email pour tous les nouveaux comptes
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={sendMail} onChange={(e)=>setSendMail(e.target.checked)} className="w-4 h-4" data-testid="import-sendmail"/>
            Envoyer immédiatement l'email de bienvenue avec identifiants
          </label>
        </div>

        <Button type="submit" disabled={loading || !file || !codeId} className="btn-shine text-white" data-testid="admin-import-submit">
          <UploadCloud size={16} className="mr-1"/> {loading ? "Import en cours…" : "Importer"}
        </Button>
      </form>

      {result && (
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 max-w-3xl" data-testid="admin-import-result">
          <h2 className="font-heading text-lg font-bold">Résultat de l'import</h2>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
                <CheckCircle2 size={16}/> Créés
              </div>
              <div className="mt-1 font-heading text-3xl font-extrabold text-emerald-800">{result.created}</div>
            </div>
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm">
                <AlertTriangle size={16}/> Ignorés
              </div>
              <div className="mt-1 font-heading text-3xl font-extrabold text-amber-800">{result.skipped.length}</div>
            </div>
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200">
              <div className="flex items-center gap-2 text-rose-700 font-semibold text-sm">
                <XCircle size={16}/> Erreurs
              </div>
              <div className="mt-1 font-heading text-3xl font-extrabold text-rose-800">{result.errors.length}</div>
            </div>
          </div>
          {(result.errors.length > 0 || result.skipped.length > 0) && (
            <div className="mt-4 text-sm">
              {result.skipped.map((s, i) => (
                <div key={`s${i}`} className="text-amber-700">Ligne {s.line} · {s.email} · {s.reason}</div>
              ))}
              {result.errors.map((s, i) => (
                <div key={`e${i}`} className="text-rose-700">Ligne {s.line} · {s.email} · {s.reason}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
